import { useEffect, useState } from "react";
import Routes from "./routes";

import { useDispatch, useSelector } from "react-redux";

import { actions } from "./store/auth/slice";
import useGqlClient from "./hooks/useGqlClient";

import {
  useAccountQuery,
  useCurrentVercelDeploymentQuery,
} from "./graphql/generated";
import "./index.css";
import { WebSocketProvider } from "./contexts/websockets";

import { usePostHog } from "posthog-js/react";
import { Toaster } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AnimatedModal } from "./components/AnimatedModal";
import { Button } from "./components/tailwind/Button";
import { authSelectors } from "./store/auth/selector";
import { useHistory, useLocation, useParams } from "react-router-dom";

function App() {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const { dealId } = useParams<{ dealId: string }>();
  const activeDealId = useSelector(authSelectors.activeDealId);
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (!activeDealId) {
      return;
    }

    if (dealId && dealId !== "") {
      return;
    }

    if (
      location.pathname.startsWith("/deal/") &&
      !location.pathname.includes("/deal/deal_")
    ) {
      const path = location.pathname.replace(
        "/deal/",
        `/deal/${activeDealId}/`,
      );
      history.replace(path);
    }
  }, [dealId, activeDealId]);

  const [openModal, setOpenModal] = useState<"update-deployment" | "">("");

  const { isSignedIn } = useSession();
  const client = useGqlClient();
  const accountQuery = useAccountQuery(
    client,
    {},
    {
      enabled: isSignedIn === true, // isSignedIn can be undefined so need to explicitly check for true
      refetchOnWindowFocus(query) {
        if (!isSignedIn) {
          return false;
        }
        return true;
      },
    },
  );

  const currentVercelDeploymentQuery = useCurrentVercelDeploymentQuery(
    client,
    {},
    {
      enabled: isSignedIn === true,
      refetchInterval: 1000 * 60 * 10,
      refetchOnWindowFocus(query) {
        if (!isSignedIn) {
          return false;
        }
        return true;
      },
      refetchIntervalInBackground: true,
    },
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      return;
    }

    if (
      currentVercelDeploymentQuery.isRefetching ||
      currentVercelDeploymentQuery.isLoading
    ) {
      return;
    }

    if (
      !currentVercelDeploymentQuery.data ||
      !currentVercelDeploymentQuery.data.currentVercelDeployment
    ) {
      return;
    }

    if (
      !import.meta.env.VITE_VERCEL_DEPLOYMENT_ID ||
      !currentVercelDeploymentQuery.data.currentVercelDeployment.id
    ) {
      return;
    }

    if (
      currentVercelDeploymentQuery.data.currentVercelDeployment.id ===
      import.meta.env.VITE_VERCEL_DEPLOYMENT_ID
    ) {
      return;
    }

    console.log(
      currentVercelDeploymentQuery.data.currentVercelDeployment.id,
      import.meta.env.VITE_VERCEL_DEPLOYMENT_ID,
    );

    setOpenModal("update-deployment");
  }, [
    isSignedIn,
    currentVercelDeploymentQuery.data,
    currentVercelDeploymentQuery.isRefetching,
    currentVercelDeploymentQuery.isLoading,
  ]);

  useEffect(() => {
    if (!accountQuery.data || !isSignedIn) {
      return;
    }

    dispatch(actions.refreshAccount({ account: accountQuery.data.account }));

    posthog?.identify(accountQuery.data.account.id, {
      email: accountQuery.data.account.email,
      name: accountQuery.data.account.name,
      firmId: accountQuery.data.account.firm
        ? accountQuery.data.account.firm.id
        : null,
    });
    if (accountQuery.data.account.firm) {
      posthog?.group("firm", `id:${accountQuery.data.account.firm.id}`, {
        name: accountQuery.data.account.firm.name,
      });
    }
  }, [
    accountQuery.data,
    accountQuery.isLoading,
    isSignedIn,
    posthog,
    dispatch,
  ]);

  // Load Plain chat widget
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://chat.cdn-plain.com/index.js";
    script.async = false;
    script.onload = () => {
      (window as any).Plain.init({
        appId: "liveChatApp_01K3229579AQ8F4A5HFGJHY87S",
        requireAuthentication: true,
        hideBranding: true,
        hideLauncher: true,
        theme: "dark",
        style: {
          brandColor: "#bfa7ff",
          brandBackgroundColor: "#873fff",
          launcherBackgroundColor: "#4d06ac",
          launcherIconColor: "#f3f1ff",
        },
      });
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if ((window as any).Plain === undefined) {
      return;
    }

    (window as any).Plain.setCustomerDetails({
      email: accountQuery.data?.account.email,
      emailHash: accountQuery.data?.account.emailHash,
      // Optional: additional customer details
      fullName: accountQuery.data?.account.name,
      shortName: accountQuery.data?.account.name,
      chatAvatarUrl: accountQuery.data?.account.avatar,
    });
  }, [accountQuery.data?.account.id, isSignedIn]);

  useEffect(() => {
    if ((window as any).Plain === undefined) {
      return;
    }

    (window as any).Plain.update({
      hideLauncher: isSignedIn,
    });
  }, [isSignedIn]);

  return (
    <WebSocketProvider>
      <ReactQueryDevtools buttonPosition="bottom-left" position="bottom" initialIsOpen={false} />

      <Toaster position="bottom-right" richColors expand />
      <div className="flex h-screen min-h-0 flex-col overflow-hidden">
        <Routes />
      </div>
      <UpdateDeploymentModal
        open={openModal === "update-deployment"}
        onClose={() => setOpenModal("")}
      />
    </WebSocketProvider>
  );
}

function UpdateDeploymentModal(props: { open: boolean; onClose: () => void }) {
  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="xl">
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-2">
          <p className="font-semibold">A new version of Liquid is available</p>
          <p className="text-gray-500 text-sm">
            Please refresh the page to update to the latest version.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            variant="positive"
            text="Refresh"
            onClick={() => {
              props.onClose();
              window.location.reload();
            }}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}
export default App;
